/**
 * Polls the acute-case counters for the ZUB calendar and raises a toast when
 * a case has arrived since this browser last looked.
 *
 * Polling rather than a push channel: SSE/websockets through Caddy on the
 * Windows host would need a reconnect story for a feature whose real-time
 * path already exists (the voice agent's reception webhook). 60 s is the
 * deliberate ceiling on in-app latency.
 */

import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { CALENDAR_CONFIG } from '@/lib/constants'
import { trpc } from '@/trpc/client'

const LAST_SEEN_KEY = 'dentora_acute_last_seen'

// Storage may throw (private browsing, enterprise policy) — the calendar must
// never white-screen over a notification convenience.
function readLastSeen(): string | null {
	try {
		return localStorage.getItem(LAST_SEEN_KEY)
	} catch {
		return null
	}
}

function writeLastSeen(value: string) {
	try {
		localStorage.setItem(LAST_SEEN_KEY, value)
	} catch {
		// Storage unavailable. readLastSeen() then returns null forever, so
		// every run would take the silent-adopt branch below and no toast would
		// ever fire — see lastSeenRef, which is what keeps notifications working.
	}
}

export function useAcuteCaseNotifier({
	enabled,
	onOpenDrawer,
}: {
	enabled: boolean
	onOpenDrawer: () => void
}) {
	const query = trpc.voiceAgent.acuteCount.useQuery(undefined, {
		enabled,
		staleTime: 30000,
		refetchOnWindowFocus: true,
		refetchInterval: CALENDAR_CONFIG.AUTO_REFRESH_INTERVAL,
	})

	// Carries the watermark in memory. This is what makes notifications work at
	// all when localStorage is unavailable: without it readLastSeen() is always
	// null, the silent-adopt branch is taken every time, and nothing is ever
	// announced. Scoped to the page load, so a reload re-adopts silently.
	const lastSeenRef = useRef<string | null>(null)
	const onOpenRef = useRef(onOpenDrawer)
	onOpenRef.current = onOpenDrawer

	const { data, error } = query
	useEffect(() => {
		if (error) console.error('[acute-desk] acuteCount query failed', error)
	}, [error])

	useEffect(() => {
		const latest = data?.latestCreatedAt
		if (!latest) return

		const lastSeen = lastSeenRef.current ?? readLastSeen()

		// First look on this browser: adopt the watermark silently. Opening the
		// calendar must not announce a week of cases that were already handled.
		if (!lastSeen) {
			lastSeenRef.current = latest
			writeLastSeen(latest)
			return
		}

		if (new Date(latest).getTime() <= new Date(lastSeen).getTime()) return

		lastSeenRef.current = latest
		writeLastSeen(latest)

		toast.warning('Nový akutní případ', {
			description: 'Telefonní asistent zaznamenal nový akutní případ.',
			// Stays until dismissed: a case that arrives while nobody is at the
			// screen must still be there when someone comes back.
			duration: Number.POSITIVE_INFINITY,
			action: {
				label: 'Otevřít',
				onClick: () => onOpenRef.current(),
			},
		})
	}, [data?.latestCreatedAt])

	// A count we could not fetch must NOT render as zero: an expired staff
	// session or a dropped network would otherwise show the surgery a clean
	// button while urgent cases sit in the database. Same reasoning as the
	// VoiceAgentOverlay's error branch in routes/calendar.tsx.
	//
	// fetchStatus === 'paused' covers offline specifically: react-query pauses
	// rather than failing, so `error` stays null and an error-only check would
	// miss it entirely.
	const isStale = query.isError || query.fetchStatus === 'paused'

	return { unresolvedCount: data?.unresolved ?? 0, isStale }
}
